import { isAdmin } from "../../guards/common/authentification-functions";
import { ApplicationRoutes } from "../../routes";
import { customDomains } from "./customDomains";
import { DEFAULT_TENANT } from "./registry";

/**
 * Deployment host schemes: "xepp-{tenant}-mt" with an optional env suffix
 * (staging), or exactly "xepp-{tenant}" (production). The production form takes
 * no hyphen, so the admin portal's "xepp-mt-prod" is not read as a tenant.
 */
const TENANT_HOST = /^xepp-(?:([a-z0-9-]+?)-mt(?:-|$)|([a-z0-9]+)$)/;

/** Shape of a tenant code: lower-case alphanumerics and hyphens. */
const TENANT_CODE = /^[a-z0-9-]+$/;

/** Lowercase the hostname and strip any port and a leading "www.". */
function normalizeHost(host: string): string {
  return host
    .toLowerCase()
    .replace(/:\d+$/, "") // strip :port
    .replace(/^www\./, ""); // strip leading www.
}

/**
 * Is the app in the BPS admin portal? BPS is its own tenant ("bps"), never the
 * host's or the JWT's — so the portal must be recognised both before login (no
 * JWT yet, and the login page is shared with the utility admin) and after.
 *
 * Pre-auth the only signal is the URL: the FIRST path segment must equal the
 * admin secret. Segment equality, not startsWith — the utility-admin secret
 * ("bps-utility") has the admin secret ("bps") as a prefix.
 */
export function isBpsAdminPortal(pathname: string = window.location.pathname): boolean {
  if (isAdmin()) return true;
  const secret = ApplicationRoutes.ADMIN_SECRET;
  // Guard the empty-env case, where every path would otherwise match.
  return !!secret && pathname.split("/")[1] === secret;
}

/**
 * Resolve the LOCAL tenant code for pre-auth pages (login/register/landing/embeds).
 * Precedence: subdomain -> custom-domain map -> ?tenant= param -> DEFAULT_TENANT.
 *
 * LOCAL branding only — never the global app tenant. Post-auth the tenant
 * comes from the JWT instead (see TenantProvider).
 *
 * The code is deliberately NOT checked against the static registry: a tenant is
 * onboarded in the backend without a frontend deploy, so an unknown code must
 * still reach the branding endpoint, which is the authority on what exists.
 *
 * @param host   Hostname, e.g. window.location.hostname (port/www tolerated).
 * @param search URL query string, e.g. window.location.search ("?tenant=aedc").
 */
export function resolveLocalTenantCode(host: string, search: string): string {
  const normalizedHost = normalizeHost(host);

  // 1) Subdomain, per the deployment scheme: "xepp-lepp-mt-staging.bpsafrica.com"
  //    or "xepp-lepp.bpsafrica.com" -> "lepp". Anchoring on the "xepp-" product
  //    prefix and either the "-mt" marker or the end of the label is what
  //    separates the tenant from the rest, so no list of known names is needed.
  //    A host that does not follow the scheme matches nothing rather than
  //    guessing at a segment.
  //    "xepp-staging" is xepp's own staging host, not a tenant named "staging".
  const label = normalizedHost.split(".")[0];
  if (label === "xepp-staging") return "xepp";
  const fromSubdomain = TENANT_HOST.exec(label);
  if (fromSubdomain) return fromSubdomain[1] ?? fromSubdomain[2];

  // 2) Custom domain: "acme.com" -> tenant via static map
  const mapped = customDomains[normalizedHost];
  if (mapped) return mapped;

  // 3) URL param (embeds on other domains): "?tenant=aedc". Shape-checked, since
  //    the value goes into the branding request path.
  const param = new URLSearchParams(search).get("tenant")?.toLowerCase();
  if (param && TENANT_CODE.test(param)) return param;

  // 4) Nothing matched -> default tenant (app looks like today)
  return DEFAULT_TENANT;
}

/**
 * Backend utility code for the current context — "bps" inside the admin portal,
 * otherwise the host/URL-derived tenant. BPS admins live in their own `bps`
 * tenant database, so their auth requests must not carry the host's utility.
 */
export function resolveUtilityName(
  host: string = window.location.hostname,
  search: string = window.location.search,
): string {
  if (isBpsAdminPortal()) return "bps";
  return resolveLocalTenantCode(host, search);
}
